import type { LivelineProps, RangeBounds } from '@morfi/chart';

/**
 * Target reveal for the Market Share chart.
 *
 * The resolution target is deliberately kept off-axis (the pinned Target
 * badge), so the Y axis fits the outcome lines and a leader far below the
 * target can still fill the plot and read as "about to resolve". While the
 * pointer hovers the chart (desktop) or a finger holds the tooltip (touch),
 * the axis eases out until the visible threshold history fits, showing how far
 * the leader still has to go; on leave it eases back to the data fit.
 *
 * The reference path remains outside the fitted range when hover ends.
 */
export const DEFAULT_TARGET_REVEAL_MS = 420;

export interface TargetReveal {
  /** Ease toward fully revealed (`true`) or hidden (`false`). Idempotent. */
  readonly setActive: (active: boolean, nowMs?: number) => void;
  /** Current eased progress in [0, 1]; cheap enough to call every frame. */
  readonly progress: (nowMs?: number) => number;
  readonly isActive: () => boolean;
}

const easeOutCubic = (p: number): number => 1 - (1 - p) ** 3;

function nowMilliseconds(explicit?: number): number {
  if (explicit !== undefined) return explicit;
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/**
 * Eased 0-1 reveal. Reversing mid-transition continues from the current
 * progress so quick enter/leave flicks never jump. Time is injectable for
 * tests.
 */
export function createTargetReveal(durationMs: number = DEFAULT_TARGET_REVEAL_MS): TargetReveal {
  let active = false;
  let from = 0;
  let to = 0;
  let startMs = 0;
  const at = (t: number): number => {
    if (durationMs <= 0) return to;
    const elapsed = t - startMs;
    if (elapsed >= durationMs) return to;
    if (elapsed <= 0) return from;
    return from + (to - from) * easeOutCubic(elapsed / durationMs);
  };
  return {
    setActive: (next: boolean, nowMs?: number): void => {
      if (next === active) return;
      const t = nowMilliseconds(nowMs);
      from = at(t);
      to = next ? 1 : 0;
      startMs = t;
      active = next;
    },
    progress: (nowMs?: number): number => at(nowMilliseconds(nowMs)),
    isActive: (): boolean => active,
  };
}

/**
 * Pure bounds policy; safe to call every animation frame. At `progress` 1 the
 * raw maximum becomes the target, so after the renderer's own margin the
 * target line sits just under the top edge of the axis.
 */
export function applyTargetReveal(
  bounds: RangeBounds,
  target: number,
  progress: number,
): RangeBounds {
  if (!(progress > 0) || !Number.isFinite(target) || !Number.isFinite(bounds.max)) return bounds;
  const gap = target - bounds.max;
  if (gap <= 0) return bounds;
  return { min: bounds.min, max: bounds.max + gap * Math.min(1, progress) };
}

/** Reveal the visible reference history, with the current target as a fallback. */
export function buildTargetRevealExtender(
  target: number,
  progress: () => number,
): LivelineProps['extendRange'] {
  if (!Number.isFinite(target)) return undefined;
  return (
    bounds: RangeBounds,
    referenceValue?: number,
    referenceBounds?: RangeBounds,
  ): RangeBounds => {
    const revealProgress = progress();
    if (!(revealProgress > 0)) return bounds;
    const revealed = applyTargetReveal(
      bounds,
      referenceBounds?.max ?? referenceValue ?? target,
      revealProgress,
    );
    const minimum = referenceBounds?.min;
    if (minimum === undefined || minimum >= bounds.min) return revealed;
    return {
      min: bounds.min + (minimum - bounds.min) * Math.min(1, revealProgress),
      max: revealed.max,
    };
  };
}
